import type { Object3D } from 'three';
import type { ExternalGeometrySourceDescriptor } from '../../model/ExternalGeometrySourceDescriptor';
import { TopologyEdgeRelocationService } from '../topology/TopologyEdgeRelocationService';
import type {
	SketchEntityReferenceSourceObjectResolverDependencies,
	SketchEntityReferenceSourceObjectResolverPort,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSourceObjectResolver';

export type {
	SketchEntityReferenceSourceObjectResolverDependencies,
	SketchEntityReferenceSourceObjectResolverPort,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSourceObjectResolver';

/**
 * 转换实体引用来源运行时对象解析器。
 *
 * 说明：
 * 1. 草图实体和旧 feature-edge 直接通过运行时访问；
 * 2. topology-edge 先解析当前 owner，再按持久化定位信息重定位；
 * 3. Executor 与批次约束恢复共用这里，避免来源解析规则泄漏或复制。
 */
export class SketchEntityReferenceSourceObjectResolver implements SketchEntityReferenceSourceObjectResolverPort {
	private readonly topologyEdgeRelocationService: TopologyEdgeRelocationService;

	/**
	 * @param dependencies 来源运行时与重定位能力。
	 */
	public constructor(private readonly dependencies: SketchEntityReferenceSourceObjectResolverDependencies) {
		this.topologyEdgeRelocationService = dependencies.topologyEdgeRelocationService ?? new TopologyEdgeRelocationService();
	}

	/**
	 * 按持久化来源描述解析当前运行时对象。
	 *
	 * @param descriptor 来源描述。
	 * @returns 当前运行时对象；无法解析时返回 null。
	 */
	public resolve(descriptor: ExternalGeometrySourceDescriptor): Object3D | null {
		if (descriptor.kind === 'sketch-entity') {
			return this.dependencies.runtime.getSketchObject(descriptor.sourceSketchId, descriptor.entityId);
		}

		if (descriptor.kind === 'feature-edge') {
			return this.dependencies.runtime.getFeatureLine(descriptor.locator.owningObjectId, descriptor.locator.featureEdgeIndex);
		}

		const ownerHost = this.dependencies.runtime.resolveTopologyOwnerHost?.(descriptor) ?? null;
		if (!ownerHost) {
			return null;
		}

		const relocationResult = this.topologyEdgeRelocationService.relocate({
			descriptor,
			host: ownerHost,
		});
		return relocationResult.status === 'failed' ? null : (relocationResult.edge.featureLine ?? null);
	}

	/**
	 * 判断当前来源是否应跳过普通重建刷新。
	 *
	 * @param descriptor 来源描述。
	 * @returns 缺少 topology owner 解析能力时返回 true。
	 */
	public shouldSkip(descriptor: ExternalGeometrySourceDescriptor): boolean {
		return descriptor.kind === 'topology-edge' && typeof this.dependencies.runtime.resolveTopologyOwnerHost !== 'function';
	}
}
